import { ApiUrls } from '@api/apiUrls';
import { IMasterAccountCreateRequest } from '@components-member/TradingAccounts/AccountCreate/components/Forms/Master/interfaces';
import { ICopyTradingSettings } from '@components-shared/socialTrading/interfaces';
import { fetchApi, FetchApiResponse } from 'api/fetchApi';
import {
  pushAccountCreateSuccessfulEvent,
  pushStartedCopyMasterSuccessEvent,
  pushStoppedCopyMasterSuccessEvent,
} from 'components/shared/googleAnalyticsEvents';
import { EmptyObject } from 'components/shared/shared.interfaces';
import { getClientSourceType } from 'components/shared/utils/getClientSourceType';
import {
  accountCreatedSuccessfullyEvent,
  IAccountCreatedResponse,
} from 'components/user/TradingAccounts/AccountCreate/requests';
import { IBaseInvestor, IMaster } from 'components/user/socialTrading/social.interfaces';
import { CLIENT_SOURCE_TYPE, TRADING_ACCOUNT_FORM_TYPE } from 'global.enums';
import { IUser } from 'global.interfaces';

interface MasterAccountCreatedResponse extends IAccountCreatedResponse {
  commissionAccountInfo: CommissionAccountInfo;
}

interface CommissionAccountInfo extends IAccountCreatedResponse {
  leverage: number;
}

export const copyMaster = async <T extends { investorId?: number }>(
  form: T,
  user: IUser,
  selectedMaster: IMaster | EmptyObject,
  selectedInvestorAccount: IBaseInvestor | EmptyObject,
): Promise<void> => {
  await fetchApi.post(`${ApiUrls.accountType.startCopying}?tradingAccountId=${form.investorId}`, form);

  stopCopyingEventInitiatedByInvestor(selectedInvestorAccount, user);

  masterAccountSuccessfullyCopiedEvent(selectedInvestorAccount.tradingAccountIdHash, user, selectedMaster);
};

export const stopCopying = async (user: IUser, investor: IBaseInvestor | EmptyObject): Promise<void> => {
  await fetchApi.post(`${ApiUrls.copyTrading.stopCopying}?investorId=${investor.id}`);

  stopCopyingEventInitiatedByInvestor(investor, user);
};

export const stopCopyingByMaster = async (investorId: number): Promise<void> => {
  await fetchApi.post(`${ApiUrls.copyTrading.stopCopyingByMaster}?investorId=${investorId}`);
};

export const createMasterAccount = async (form: IMasterAccountCreateRequest, user: IUser): Promise<void> => {
  const response = await fetchApi.post<MasterAccountCreatedResponse>(
    ApiUrls.tradingAccountUser.masterAccountCreate,
    form,
  );

  masterAndCommissionAccountInfoEvents(response.data, form, user);
};

export const createInvestorAccount = async <T extends { leverage?: string | number }>(
  form: T,
  user: IUser,
  selectedMaster: IMaster | EmptyObject,
): Promise<void> => {
  const response = await fetchApi.post<IAccountCreatedResponse>(ApiUrls.tradingAccountUser.investorAccountCreate, form);

  //account created
  accountCreatedSuccessfullyEvent(response.data.idHash, form, user, TRADING_ACCOUNT_FORM_TYPE.INVESTOR);

  //investor copies master
  masterAccountSuccessfullyCopiedEvent(response.data.idHash, user, selectedMaster);
};

const masterAndCommissionAccountInfoEvents = (
  response: MasterAccountCreatedResponse,
  form: IMasterAccountCreateRequest,
  user: IUser,
): void => {
  accountCreatedSuccessfullyEvent(response.idHash, form, user, TRADING_ACCOUNT_FORM_TYPE.MASTER);

  const { commissionAccountInfo } = response;

  //if already existing commission account was used for creating master
  if (!commissionAccountInfo) {
    return;
  }

  commissionAccountCreateSuccessfulEvent(
    user.idHash,
    user.profile.clientSourceType,
    commissionAccountInfo.idHash,
    commissionAccountInfo.accountType,
    commissionAccountInfo.leverage,
    user.profile.status,
  );
};

const commissionAccountCreateSuccessfulEvent = (
  userIdHash: string,
  clientSourceType: CLIENT_SOURCE_TYPE,
  commissionAccIdHash: string,
  commissionAccType: string,
  commissionAccLeverage: number,
  status: string,
) => {
  pushAccountCreateSuccessfulEvent(
    userIdHash,
    getClientSourceType(clientSourceType),
    commissionAccIdHash,
    commissionAccType,
    status,
    commissionAccLeverage,
  );
};

const masterAccountSuccessfullyCopiedEvent = (
  investorAccountIdHash: string,
  user: IUser,
  selectedMaster: IMaster | EmptyObject,
): void => {
  pushStartedCopyMasterSuccessEvent(
    user.idHash,
    getClientSourceType(user.profile.clientSourceType),
    investorAccountIdHash,
    TRADING_ACCOUNT_FORM_TYPE.INVESTOR,
    selectedMaster.tradingAccountIdHash,
    selectedMaster.userIdHash,
  );
};

//currently stop copying can be done only by investor
const stopCopyingEventInitiatedByInvestor = (
  selectedInvestorAccount: IBaseInvestor | EmptyObject,
  user: IUser,
): void => {
  if (selectedInvestorAccount.masterInfo == null) {
    return;
  }

  pushStoppedCopyMasterSuccessEvent(
    user.idHash,
    getClientSourceType(user.profile.clientSourceType),
    selectedInvestorAccount.tradingAccountIdHash,
    TRADING_ACCOUNT_FORM_TYPE.INVESTOR,
    selectedInvestorAccount.masterInfo.tradingAccountIdHash,
    selectedInvestorAccount.masterInfo.userIdHash,
  );
};

export const getCopyTradingSettings = async (): Promise<FetchApiResponse<ICopyTradingSettings>> => {
  return await fetchApi.get<ICopyTradingSettings>(ApiUrls.copyTradingSettings.get);
};
